<template>
  <div ref="sonarSidebar" class="flat-scroll sonar-sidebar">
    <div v-if="showQuestions" class="sonar-sidebar__running">
      <SonarQuestion
        v-bind="{
          currentQuestion,
          isLabQuestion,
        }"
      />
      <SonarAnswers
        ref="answers"
        class="sonar-sidebar__answers"
        v-bind="{
          currentQuestion,
          lastQuestion,
          firstQuestion,
          showActionButtons: true,
          isLastQuestion,
        }"
      />
    </div>
    <div v-else class="sonar-sidebar__starting-lab-wrapper">
      <InfoIcon
        v-if="!showWelcomeCard && !isChangingLabState"
        class="sonar-sidebar__starting-lab-icon"
      />
      <ActionLoader
        v-if="isChangingLabState"
        :name="'ball-beat'"
        :color="'#ffffff'"
        :size="1"
      />
      <div class="sonar-sidebar__starting-lab-info">
        {{ labQuestionsLabel }}
      </div>
      <button
        v-if="(showWelcomeCard || isLabRunning) && !isChangingLabState"
        :disabled="isCtaLoading"
        class="
            btn
            btn--primary
          "
        :class="ctaBtnClasses"
        @click="clickHandler()"
      >
        {{ ctaLabel }}
        <ActionLoader
          v-if="isCtaLoading"
          name="ball-beat"
          color="#000"
          class="sonar-sidebar__cta-loader"
        />
        <ArrowRightIcon v-if="ctaWithArrow" />
      </button>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import { InfoIcon, ArrowRightIcon } from 'vue-feather-icons'
import CONSTANTS from '@/constants'
import ActionLoader from '@/components/shared/ActionLoader.vue'
import SonarAnswers from './SonarAnswers.vue'
import SonarQuestion from './SonarQuestion.vue'

export default {
  name: 'SonarAssessmentSidebar',
  components: {
    SonarAnswers,
    SonarQuestion,
    InfoIcon,
    ArrowRightIcon,
    ActionLoader,
  },
  props: {
    showWelcomeCard: {
      type: Boolean,
      required: true,
      default: false,
    },
    isLabRunning: {
      type: Boolean,
      required: true,
      default: false,
    },
    isCtaLoading: {
      type: Boolean,
      required: true,
      default: false,
    },
    isAssessmentRunning: {
      type: Boolean,
      required: true,
      default: false,
    },
  },
  data() {
    return {
      isCtaShowed: true,
    }
  },
  computed: {
    ...mapGetters({
      question: 'sonar/currentQuestion',
      assessmentAttempt: 'sonar/assessmentAttempt',
      assessments: 'sonar/assessments',
      canAnswerQuestions: 'sonar/canAnswerQuestions',
      isChangingLabState: 'sonar/isChangingLabState',
      isStartingLab: 'sonar/isStartingLab',
      isRestartingLab: 'sonar/isRestartingLab',
      isSwitchingLabs: 'sonar/isSwitchingLabs',
    }),
    showQuestions() {
      return this.isAssessmentRunning && this.canAnswerQuestions
    },
    currentQuestion() {
      const question = this.question
      const dunnoOptionExists = this.question?.answers?.some(
        answer => answer.id === CONSTANTS.SONAR.ASSESSMENT.ANSWER.DONT_KNOW
      )
      if (question?.question_type === 'multiple_choice' && !dunnoOptionExists) {
        question.answers.push({
          content_html: "<p>I don't know</p>",
          id: CONSTANTS.SONAR.ASSESSMENT.ANSWER.DONT_KNOW,
        })
      }
      return question
    },
    ctaLabel() {
      return this.showWelcomeCard ? 'Get started' : 'Go to questions'
    },
    ctaBtnClasses() {
      return this.ctaWithArrow
        ? `btn--primary--with-icon btn--primary--with-icon--right btn--default-icon-color`
        : ''
    },
    ctaWithArrow() {
      return !this.showWelcomeCard
    },
    currentPosition() {
      return this.currentQuestion.position
    },
    totalQuestions() {
      return this.assessmentAttempt.questions.length
    },
    lastQuestion() {
      return this.assessmentAttempt.questions.at(-1)
    },
    firstQuestion() {
      return this.assessmentAttempt.questions[0]
    },
    isLastQuestion() {
      return this.totalQuestions === this.currentPosition
    },
    labQuestionsQty() {
      return this.assessmentAttempt.questions.filter(question => {
        return (
          question.labData?.content_id ===
          this.currentQuestion.labData?.content_id
        )
      }).length
    },
    isLabQuestion() {
      return this.currentQuestion.hasLab
    },
    labQuestionsQtyLabel() {
      const plural = this.labQuestionsQty > 1 ? 's are' : ' is'
      return `${
        this.labQuestionsQty > 1 ? this.labQuestionsQty : ''
      } question${plural}`
    },
    labQuestionsLabel() {
      if (this.showWelcomeCard)
        return 'Before you begin, take a moment to read the overview carefully.'
      if (this.isStartingLab)
        return 'This lab is being built and will be ready soon!'
      if (this.isRestartingLab) return 'This lab is being restarted'
      if (this.isLabRunning) return 'Lab running!'
      const shouldStartLabLabel = !this.isSwitchingLabs
        ? 'to the lab. Start the lab to continue.'
        : 'to another lab. This lab is being built and will be ready soon!'
      return `The following ${this.labQuestionsQtyLabel} related ${shouldStartLabLabel}`
    },
  },
  watch: {
    currentQuestion() {
      this.$refs.sonarSidebar.scrollTop = 0
    },
  },
  methods: {
    clickHandler() {
      if (this.showWelcomeCard) this.$parent.$emit('start-assessment')
      if (this.isLabRunning) this.$parent.$emit('resume-assessment')
    },
  },
}
</script>

<style lang="scss" scoped>
.sonar-sidebar {
  height: 100%;
  &__wrapper {
    display: flex;
    justify-content: center;
    height: 100%;
  }
  &__running {
    width: 100%;
    max-width: 1200px;
    padding: 20px;
    margin: auto;
  }
  &__starting-lab-wrapper {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 20px;
    padding: 40px;
    height: 100%;
  }
  &__starting-lab-icon {
    display: flex;
    justify-content: center;
    color: var(--tundora-gray);
    width: 80px;
    height: 80px;
  }
  &__starting-lab-info {
    display: flex;
    justify-content: center;
    color: var(--white);
    font-size: rem(18);
    line-height: rem(24);
    text-align: center;
  }
  &__start-questions-card {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 20px;
    height: 100%;
  }
  &__start-questions-label {
    color: var(--white);
    font-size: rem(18);
    line-height: rem(24);
  }
  &__cta-loader {
    display: flex;
  }
  &__answers {
    width: 100%;
    padding: 0;
    @include tablet {
      padding: 0 0 40px 0;
    }
    .answers {
      &__selection {
        height: 65px;
        .answers__select {
          padding: unset;
          padding-left: 20px;
          align-self: center;
          p {
            margin: unset;
          }
        }
      }
    }
  }
}
</style>
